import { formatDate, formatTime } from './dateTime';
import { Drill, Practice, Coach } from '../types';

// Helper type for drill coach assignments
type DrillCoachMap = Record<string, string>;

// Print practice schedule as a table
export const printPracticeSchedule = (
  practice: Practice,
  drills: Drill[],
  coaches: Coach[],
  drillCoaches: DrillCoachMap,
  timeFormat: '12h' | '24h' = '12h'
): void => {
  // Create a new window for printing
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    console.error('Failed to open print window');
    return;
  }

  // Create the print container
  const printContainer = document.createElement('div');
  printContainer.className = 'print-container p-4';

  // Add practice header
  const headerContainer = document.createElement('div');
  headerContainer.className = 'mb-4';

  const practiceTitle = document.createElement('h1');
  practiceTitle.className = 'text-lg font-bold';
  practiceTitle.textContent = practice.theme ?? 'Team Day';
  headerContainer.appendChild(practiceTitle);

  const practiceInfo = document.createElement('div');
  practiceInfo.className = 'text-sm';
  const formattedDate = formatDate(practice.date);
  practiceInfo.textContent = `Date: ${formattedDate} | Time: ${formatTime(practice.start_time, timeFormat)} - ${formatTime(practice.end_time, timeFormat)} | Location: ${practice.location}`;
  headerContainer.appendChild(practiceInfo);

  printContainer.appendChild(headerContainer);

  // Create the table
  const table = document.createElement('table');
  table.className = 'w-full border-collapse text-xs';

  // Create table header
  const thead = document.createElement('thead');
  const headerRow = document.createElement('tr');
  headerRow.className = 'bg-gray-100 border-b-2 border-black';

  const headers = ['Time', 'Drill Focus', 'Position', 'Drill Details', 'Coach'];
  headers.forEach(headerText => {
    const th = document.createElement('th');
    th.className = 'p-1 text-left border border-black font-medium';
    th.textContent = headerText;
    headerRow.appendChild(th);
  });

  thead.appendChild(headerRow);
  table.appendChild(thead);

  // Create table body
  const tbody = document.createElement('tbody');
  let currentTime = 0;

  drills.forEach((drill) => {
    const isWaterBreak = drill.name.toLowerCase().includes('water break');
    
    // Find "ALL" position group if it exists
    const allPositionGroup = drill.position_groups?.find(pg => 
      typeof pg === 'string' ? pg === 'ALL' : pg.group_name === 'ALL'
    );
    
    // Water breaks and drills with ALL position group
    if (isWaterBreak || allPositionGroup) {
      const row = document.createElement('tr');
      if (isWaterBreak) row.className = 'bg-blue-50';
      row.className += ' border border-black';

      // Time column
      const timeCell = document.createElement('td');
      timeCell.className = 'p-1 border border-black';
      const startTime = formatMinutes(currentTime, practice.start_time, timeFormat);
      const endTime = formatMinutes(currentTime + drill.duration, practice.start_time, timeFormat);
      timeCell.textContent = `${startTime}-${endTime} (${drill.duration}min)`;
      row.appendChild(timeCell);

      // Drill Focus column
      const focusCell = document.createElement('td');
      focusCell.className = 'p-1 border border-black';
      focusCell.textContent = drill.name;
      row.appendChild(focusCell);

      // Position column
      const positionCell = document.createElement('td');
      positionCell.className = 'p-1 border border-black text-center';
      positionCell.textContent = 'ALL';
      row.appendChild(positionCell);

      // Drill Details column
      const detailsCell = document.createElement('td');
      detailsCell.className = 'p-1 border border-black';
      if (allPositionGroup && typeof allPositionGroup !== 'string' && allPositionGroup.steps) {
        const stepsList = document.createElement('ul');
        stepsList.className = 'list-disc list-inside';
        allPositionGroup.steps.forEach(step => {
          const stepItem = document.createElement('li');
          stepItem.textContent = step.name;
          stepsList.appendChild(stepItem);
        });
        detailsCell.appendChild(stepsList);
      }
      row.appendChild(detailsCell);

      // Coach column
      const coachCell = document.createElement('td');
      coachCell.className = 'p-1 border border-black';
      const coachIds = drillCoaches[`${drill.id}-ALL`] || [];
      if (coachIds.length > 0) {
        const coachNames = coachIds.map(coachId => {
          const coach = coaches.find(c => c.id === coachId);
          return coach ? coach.name : 'Unknown';
        }).join(', ');
        coachCell.textContent = coachNames;
      } else {
        coachCell.textContent = 'ALL';
      }
      row.appendChild(coachCell);

      tbody.appendChild(row);
      currentTime += drill.duration;
    }
    // For position-specific drills (show all position groups except ALL)
    else if (drill.position_groups) {
      const positionGroups = drill.position_groups.filter(pg => 
        typeof pg === 'string' ? pg !== 'ALL' : pg.group_name !== 'ALL'
      );
      
      positionGroups.forEach((group) => {
        const groupName = typeof group === 'string' ? group : group.group_name;
        const groupSteps = typeof group === 'string' ? [] : group.steps;
        
        const row = document.createElement('tr');
        row.className = 'border border-black';

        // Time column
        const timeCell = document.createElement('td');
        timeCell.className = 'p-1 border border-black';
        const startTime = formatMinutes(currentTime, practice.start_time, timeFormat);
        const endTime = formatMinutes(currentTime + drill.duration, practice.start_time, timeFormat);
        timeCell.textContent = `${startTime}-${endTime} (${drill.duration}min)`;
        row.appendChild(timeCell);

        // Drill Focus column
        const focusCell = document.createElement('td');
        focusCell.className = 'p-1 border border-black';
        focusCell.textContent = drill.name;
        row.appendChild(focusCell);

        // Position column
        const positionCell = document.createElement('td');
        positionCell.className = 'p-1 border border-black text-center';
        positionCell.textContent = groupName;
        row.appendChild(positionCell);

        // Drill Details column
        const detailsCell = document.createElement('td');
        detailsCell.className = 'p-1 border border-black';
        if (groupSteps && groupSteps.length > 0) {
          const stepsList = document.createElement('ul');
          stepsList.className = 'list-disc list-inside';
          groupSteps.forEach(step => {
            const stepItem = document.createElement('li');
            stepItem.textContent = step.name;
            stepsList.appendChild(stepItem);
          });
          detailsCell.appendChild(stepsList);
        }
        row.appendChild(detailsCell);

        // Coach column
        const coachCell = document.createElement('td');
        coachCell.className = 'p-1 border border-black';
        const coachIds = drillCoaches[`${drill.id}-${groupName}`] || [];
        if (coachIds.length > 0) {
          const coachNames = coachIds.map(coachId => {
            const coach = coaches.find(c => c.id === coachId);
            return coach ? coach.name : 'Unknown';
          }).join(', ');
          coachCell.textContent = coachNames;
        } else {
          coachCell.textContent = 'ALL';
        }
        row.appendChild(coachCell);

        tbody.appendChild(row);
      });
      
      // Only increment time once per drill, not for each position group
      currentTime += drill.duration;
    }
  });

  table.appendChild(tbody);
  printContainer.appendChild(table);

  // Add CSS styles
  const style = document.createElement('style');
  style.textContent = `
    @media print {
      body { margin: 0; padding: 0; }
      .print-container { padding: 10px; }
      table { width: 100%; border-collapse: collapse; font-size: 10px; }
      th, td { border: 1px solid #000; padding: 4px; text-align: left; }
      .bg-blue-50 { background-color: #f0f9ff !important; }
      h1 { margin-bottom: 8px; font-size: 14px; }
      .list-disc { margin: 0; padding-left: 12px; }
      li { margin: 0; padding: 0; }
      .text-center { text-align: center; }
      tr { border: 1px solid #000; }
    }
  `;
  printContainer.appendChild(style);

  // Write the content to the new window
  printWindow.document.write(printContainer.outerHTML);
  printWindow.document.close();

  // Print the window
  printWindow.print();
};

// Format minutes into time
const formatMinutes = (minutes: number, startTime: string, timeFormat: '12h' | '24h'): string => {
  if (!startTime) return '';
  
  try {
    const [hours, mins] = startTime.split(':').map(Number);
    const totalMinutes = hours * 60 + mins + minutes;

    const newHours = Math.floor(totalMinutes / 60) % 24;
    const newMinutes = totalMinutes % 60;

    if (timeFormat === '12h') {
      const displayHours = newHours % 12 || 12;
      return `${displayHours}:${newMinutes.toString().padStart(2, '0')}`;
    } else {
      return `${newHours.toString().padStart(2, '0')}:${newMinutes.toString().padStart(2, '0')}`;
    }
  } catch (error) {
    console.error('Error formatting minutes:', error);
    return '';
  }
}; 